/**
 * "Adicionar round" slide-over — attach new evidence to a tracked candidate.
 * Only a LinkedIn packet re-scores today (the round enters `evaluating`); CV /
 * transcript / free-text are recorded as first-class evidence and carry the
 * level forward (the pending-fusion state) until the scoring-fusion phase lands.
 */

import type { InformationPacketType } from "@shared/labs/level-assessment-candidate";
import { FileText, Linkedin, MessageSquare, Plus, Text } from "lucide-react";
import { useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";
import {
  C,
  defaultPacketLabel,
  ErrorText,
  Field,
  GhostButton,
  MONO,
  OverlayActions,
  OverlayHeader,
  PrimaryButton,
  SlideoverShell,
  TextArea,
  TextInput,
  UI,
} from "./candidate-ui";

const TYPES: { type: InformationPacketType; label: string; icon: typeof Linkedin }[] = [
  { type: "linkedin", label: "URL LinkedIn", icon: Linkedin },
  { type: "cv", label: "CV", icon: FileText },
  { type: "interview_transcript", label: "Transcrição", icon: MessageSquare },
  { type: "free_text", label: "Texto livre", icon: Text },
];

const TEXT_PLACEHOLDER: Record<Exclude<InformationPacketType, "linkedin">, string> = {
  cv: "Cole o texto do CV ou descreva a experiência…",
  interview_transcript: "Cole a transcrição da entrevista…",
  free_text: "Escreva observações ou trajetória em texto livre…",
};

export function AddRoundPanel({
  candidateId,
  onClose,
  onAdded,
}: {
  candidateId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [type, setType] = useState<InformationPacketType>("linkedin");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [content, setContent] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  async function onSubmit() {
    setError(null);
    // Mirror the packet schema's refinements client-side.
    if (type === "linkedin" && !linkedinUrl.trim()) return setError("Cole o perfil do LinkedIn.");
    if (type !== "linkedin" && !content.trim())
      return setError("Adicione o conteúdo da evidência.");

    const packetLabel = label.trim() || defaultPacketLabel(type);
    const packet =
      type === "linkedin"
        ? { type, label: packetLabel, linkedinUrl: linkedinUrl.trim() }
        : { type, label: packetLabel, content: content.trim() };

    setSubmitting(true);
    try {
      await rpcClient.labs.levelAssessmentCandidateAddRound({
        candidateId,
        label: label.trim() || undefined,
        packets: [packet],
      });
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao adicionar o round.");
      setSubmitting(false);
    }
  }

  return (
    <SlideoverShell onClose={onClose}>
      <OverlayHeader
        title="Adicionar round"
        subtitle="Anexe novas evidências. Só o perfil do LinkedIn recalcula o score hoje; CV e transcrições ficam registrados para a fase de fusão."
        onClose={onClose}
      />

      <Field label="Tipo de evidência">
        <div
          style={{
            display: "inline-flex",
            flexWrap: "wrap",
            background: C.ink5,
            borderRadius: 10,
            padding: 4,
            gap: 2,
          }}
        >
          {TYPES.map((t) => {
            const Icon = t.icon;
            const on = t.type === type;
            return (
              <button
                key={t.type}
                type="button"
                onClick={() => setType(t.type)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  ...UI,
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "8px 13px",
                  borderRadius: 7,
                  border: "none",
                  cursor: "pointer",
                  color: on ? C.ink : C.ink50,
                  background: on ? C.white : "transparent",
                  boxShadow: on ? `0 1px 2px ${C.ink10}` : "none",
                }}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </Field>

      {type === "linkedin" ? (
        <Field
          label="Perfil do LinkedIn"
          hint="Recalcula o nível — o round entrará em “Avaliando…” até concluir."
        >
          <TextInput
            value={linkedinUrl}
            onChange={setLinkedinUrl}
            placeholder="linkedin.com/in/candidato"
          />
        </Field>
      ) : (
        <Field
          label="Conteúdo"
          hint={
            <span style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
              <span style={{ color: C.violet, flex: "0 0 auto" }}>●</span>
              Registrado, mas ainda não entra no score (fase de fusão). O nível será carregado do
              último round pontuado.
            </span>
          }
        >
          <TextArea
            value={content}
            onChange={setContent}
            placeholder={TEXT_PLACEHOLDER[type as Exclude<InformationPacketType, "linkedin">]}
          />
        </Field>
      )}

      <Field
        label={
          <span>
            Rótulo do round{" "}
            <span style={{ ...MONO, color: C.ink50, fontWeight: 400 }}>(opcional)</span>
          </span>
        }
      >
        <TextInput value={label} onChange={setLabel} placeholder="ex.: Entrevista técnica" />
      </Field>

      {error && <ErrorText>{error}</ErrorText>}
      <OverlayActions>
        <GhostButton onClick={onClose} disabled={submitting}>
          Cancelar
        </GhostButton>
        <PrimaryButton onClick={onSubmit} disabled={submitting}>
          <Plus size={16} /> {submitting ? "Adicionando…" : "Adicionar round"}
        </PrimaryButton>
      </OverlayActions>
    </SlideoverShell>
  );
}
